import { useEffect, useRef } from 'react'
import { useSetState } from 'ahooks'
import { Button, DatePicker, Form, message, TreeSelect, Upload } from 'antd'
import {
  SActionBtn,
  SBack,
  SDrawerOrModal,
  SFuncModal,
  SFuncModalFormRef,
  SLocalTreeSelected,
  SLowcodeList,
  SMessage,
  STableActions,
} from 'shj-components'
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons'

import { httpOrgList } from '@/services/homeService.ts'
import {
  getPriceItemSpecialConfigPage,
  getProjectPage,
  priceItemSpecialConfigBatchDelete,
  priceItemSpecialConfigDelete,
  priceItemSpecialConfigImport,
  priceSpecialConfigBatchUpdateDate,
} from '@/services/product.ts'
import { config } from './config.tsx'
import AddOrEditConfigure from './addOrEdit'
import { apiData2Table } from '@/utils/data.ts'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { debounce, get } from 'lodash-es'
import { fileDownload } from '@/utils/download.ts'
import UrlConfig from '@/api/UrlConfigs.ts'
import dayjs from 'dayjs'

const SpecialConfigTable = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [search] = useSearchParams()
  const name = search.get('name')
  const mathType = search.get('mathType')
  const hasParent = search.get('hasParent')
  const type = search.get('type')
  const listRef = useRef()
  const editRef = useRef<SFuncModalFormRef>({ onFinish: undefined })
  const [form] = Form.useForm()

  const [state, setState] = useSetState<any>({
    exportLoading: false,
    loading: false,
    visible: false,
    record: {},
    companyList: [],
  })

  useEffect(() => {
    getOptions()
  }, [])

  const getOptions = async () => {
    const companyList = await httpOrgList()
    setState({ companyList: [{ id: '0', name: '通用' }, ...companyList] })
  }

  const fetchList = ({ current, pageSize, query }) => {
    if (!id) return
    return new Promise((resolve, reject) => {
      getPriceItemSpecialConfigPage({
        params: { pageId: current, pageSize },
        data: { ...query, priceItemId: id, searchFlag: true },
      })
        .then(res => {
          resolve(apiData2Table(res))
        })
        .catch(reject)
    })
  }

  const onAddOrEdit = (record = {}) => {
    setState({ visible: true, record: { ...record } })
  }

  const delConfirm = apiFun => {
    SMessage.confirm({
      onOk: () => {
        return apiFun().then((res: any) => {
          message.success(res.message || '操作成功')
          listRef.current?.reload()
        })
      },
    })
  }

  const onDel = (id: string) => {
    delConfirm(() => priceItemSpecialConfigDelete(id))
  }

  const onBatchDel = ({ selectedRowKeys }: { selectedRowKeys: string[] }) => {
    delConfirm(() => priceItemSpecialConfigBatchDelete(selectedRowKeys))
  }

  const onSubmit = () => {
    editRef.current.onFinish?.()
  }

  const onCancel = () => {
    setState({ visible: false, loading: false })
  }

  const onImport = (upload: any) => {
    const { file, onSuccess, onError } = upload
    const data = new FormData()
    data.append('file', file)
    data.append('priceItemId', id)
    return priceItemSpecialConfigImport(data)
      .then((res: any) => {
        const msg = get(res, 'data.message') || ''
        listRef.current && listRef.current.reload()
        onSuccess && onSuccess()
        message.success(msg || '导入成功')
      })
      .catch(onError)
  }

  const onExport = () => {
    setState({ exportLoading: true })
    fileDownload(UrlConfig.PlusProductWeb.PriceItemSpecialConfigDownload)
      .then(() => setState({ exportLoading: false }))
      .catch(() => setState({ exportLoading: false }))
  }

  const goBack = () => {
    navigate(`/config/priceTagManage?type=${type}`, { state: { saveCache: true } })
  }
  const onBatchUpDateStart = ({ selectedRowKeys }: { selectedRowKeys: string[] }) => {
    postData({
      ids: selectedRowKeys,
      type: 'startDate',
      label: '开始日期',
      required: true,
    })
  }
  const onBatchUpDateEnd = ({ selectedRowKeys }: { selectedRowKeys: string[] }) => {
    postData({
      ids: selectedRowKeys,
      type: 'endDate',
      label: '结束日期',
      required: false,
    })
  }
  const postData = data => {
    const dialog = SFuncModal({
      title: '修改日期',
      content: (
        <Form form={form}>
          <Form.Item
            label={data.label}
            name="date"
            required={data.required}
            rules={[{ required: data.required, message: data.label + '不能为空' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      ),
      onOk: () => {
        form.validateFields().then((values: Record<string, string>) => {
          values.date = values.date && dayjs(values.date).format('YYYY-MM-DD')
            priceSpecialConfigBatchUpdateDate({
              data: {
                ...values,
                ids: data.ids,
                type: data.type,
              },
            })
              .then(res => {
                message.success('操作成功')
                form.resetFields()
                listRef.current?.reload()
                dialog.destroy()
              })
              .catch(() => {
                form.resetFields()
                dialog.destroy()
              })
        
        })
      },
      onCancel: () => {
        form.resetFields()
        dialog.destroy()
      },
    })
  }
  const extensions = {
    request: { fetchList },
    events: { onAdd: () => onAddOrEdit(), onBatchDel, onBatchUpDateStart, onBatchUpDateEnd },
    customRender: {
      sortRender(v, r, s) {
        return s + 1
      },
      jsonRender(v: any) {
        const { shops: list } = v && JSON.parse(v)
        return list?.map(item => item.name)?.join(', ')
      },
      actionRender(_: null, record: Record<string, any>) {
        return (
          <STableActions>
            <SActionBtn onClick={() => onAddOrEdit(record)}>编辑</SActionBtn>
            <SActionBtn danger onClick={() => onDel(record.id)}>
              删除
            </SActionBtn>
          </STableActions>
        )
      },
      importRender: () => {
        return (
          <Upload listType="text" customRequest={onImport} itemRender={() => null}>
            <Button type={'primary'} icon={<UploadOutlined />}>
              批量导入
            </Button>
          </Upload>
        )
      },
      exportRender: () => {
        return (
          <Button icon={<DownloadOutlined />} onClick={onExport} loading={state.exportLoading}>
            下载导入模板
          </Button>
        )
      },
      shopsRender: ({ name, form }) => {
        return (
          <SLocalTreeSelected
            treeDefaultExpandAll
            placeholder={'请选择门店'}
            showCheckedStrategy={TreeSelect.SHOW_PARENT}
            fieldNames={{ label: 'name', value: 'id' }}
            onChange={v => form.setFieldsValue({ [name]: v })}
            treeData={state.companyList}
          />
        )
      },
      nameRender() {
        return name
      },
      switchRender(v) {
        return typeof v === 'boolean' ? (v ? '显示' : '不显示') : ''
      },
    },
  }

  return (
    <SBack title={`${name}特殊配置`} level={2} goPath={goBack}>
      <div>
        <SLowcodeList ref={listRef} searchMode={'group'} config={config} {...extensions} />
        <SDrawerOrModal
          mode={'modal'}
          onCancel={onCancel}
          onOk={onSubmit}
          width={710}
          open={state.visible}
          loading={state.loading}
          title={(state.record.id ? '编辑' : '新增') + '配置'}
        >
          <AddOrEditConfigure
            fn={listRef.current?.reload}
            onClose={onCancel}
            editRef={editRef}
            mode={'modal'}
            priceItemId={id}
            mathType={mathType}
            hasParent={hasParent}
            companyList={state.companyList}
            record={state.record}
            setLoading={(loading: boolean) => setState({ loading })}
          />
        </SDrawerOrModal>
      </div>
    </SBack>
  )
}

export default SpecialConfigTable
